import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { If } from "react-conditional-components-renderer";
import AscPortal from "../../AscPortal/AscPortal";
import style from "./AscPopoverAndOptionsWrapper.module.scss";

export type AscPopoverAndOptionsWrapperPropsAlignment = "left" | "right";
export interface AscPopoverAndOptionsWrapperProps
  extends Omit<
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >,
    "className" | "children"
  > {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  content: React.ReactNode;
  visible: boolean;
  onClickOutsideContainer?: (event: MouseEvent) => void;
  toggleIconContainerRef?: React.RefObject<HTMLDivElement>;
  fullWidth?: boolean;
  alignment?: AscPopoverAndOptionsWrapperPropsAlignment;
}

/**
 * Wrapper component for AscPopover and it's options (AscPopoverOptions).
 */
export default function AscPopoverAndOptionsWrapper({
  children,
  className,
  contentClassName,
  content,
  visible,
  toggleIconContainerRef,
  onClickOutsideContainer,
  fullWidth = false,
  alignment = "left",
  ...rest
}: AscPopoverAndOptionsWrapperProps) {
  const [coordinates, setCoordinates] = useState({
    left: 0,
    top: 0,
    right: 0,
  });
  const [contentWidth, setContentWidth] = useState<number>();
  const containerRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentContainerRef.current) {
      /**
       * To hide content during calculation to avoid the
       * flashing of the content before it has been positioned
       * at the appropriate coordinates
       * */
      contentContainerRef.current.style.visibility = `hidden`;
    }

    const containerRect = containerRef?.current?.getBoundingClientRect();
    const contentContainerRect =
      contentContainerRef?.current?.getBoundingClientRect();
    const verticalOffset = 8;

    if (!containerRect || !contentContainerRect) {
      return;
    }

    const left = containerRect.x;
    const right = window.innerWidth - containerRect.right;
    const top =
      containerRect.y + containerRect.height + window.scrollY + verticalOffset;
    setCoordinates({
      top,
      left,
      right,
    });
    setContentWidth(containerRect?.width);
  }, [containerRef, visible]);

  useEffect(() => {
    if (!contentContainerRef?.current) {
      return;
    }
    if (alignment === "left") {
      contentContainerRef.current.style.left = `${coordinates?.left}px`;
    } else {
      contentContainerRef.current.style.right = `${coordinates?.right}px`;
    }
    contentContainerRef.current.style.top = `${coordinates?.top}px`;
    contentContainerRef.current.style.visibility = `visible`;
    if (fullWidth) {
      contentContainerRef.current.style.width = `${contentWidth}px`;
    }
  }, [coordinates, contentWidth, fullWidth, alignment]);

  useEffect(() => {
    function handleClickOutsideContainer(e: MouseEvent) {
      if (
        (containerRef?.current?.contains(e.target as Node) ||
          contentContainerRef?.current?.contains(e.target as Node)) &&
        (toggleIconContainerRef?.current !== e?.target ||
          !toggleIconContainerRef?.current?.contains(e.target as Node))
      ) {
        return;
      }
      onClickOutsideContainer?.(e);
    }

    window.addEventListener("click", handleClickOutsideContainer);

    return () => {
      window.removeEventListener("click", handleClickOutsideContainer);
    };
  }, [onClickOutsideContainer, toggleIconContainerRef]);

  return (
    <div
      className={classNames(style.ground, className)}
      ref={containerRef}
      {...rest}
    >
      {children}
      <If test={visible}>
        <AscPortal>
          <div
            ref={contentContainerRef}
            className={classNames(style.content, contentClassName)}
          >
            {content}
          </div>
        </AscPortal>
      </If>
    </div>
  );
}
